import React, { useEffect, useMemo, useState } from 'react';
import { paymentConfigApi, smtpSettingsApi, storesApi, tenantsApi, usersApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const DEFAULT_THEME = {
    primary: '#ff9f2f',
    primary700: '#f08a17',
    darkBtn: '#15395b',
};

function applyTheme(theme) {
    const root = document.documentElement;
    if (!root) return;
    root.style.setProperty('--primary', theme.primary || DEFAULT_THEME.primary);
    root.style.setProperty('--primary-700', theme.primary700 || DEFAULT_THEME.primary700);
    root.style.setProperty('--dark-btn', theme.darkBtn || DEFAULT_THEME.darkBtn);
}

export default function AppSettingsPage() {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [tenant, setTenant] = useState(null);
    const [stores, setStores] = useState([]);
    const [storeInfo, setStoreInfo] = useState({
        name: '',
        email: '',
        phone: '',
        logo_url: '',
        business_type: '',
        address: '',
    });
    const [theme, setTheme] = useState(DEFAULT_THEME);
    const [branchForm, setBranchForm] = useState({
        name: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        country: 'Nigeria',
        store_type: 'retail',
    });
    const [taxForm, setTaxForm] = useState({ store_id: '', tax_rate: '0' });
    const [selectedStoreId, setSelectedStoreId] = useState('');
    const [paymentConfig, setPaymentConfig] = useState({
        storeFrontendEnabled: false,
        paystack: { enabled: false, publicKey: '', secretKey: '' },
        flutterwave: { enabled: false, publicKey: '', secretKey: '', encryptionKey: '' },
        bankTransfer: {
            enabled: false,
            bankName: '',
            accountName: '',
            accountNumber: '',
            receiptRequired: true,
            instructions: '',
        },
    });
    const [adminForm, setAdminForm] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        password: '',
        role: 'manager',
    });
    const [smtpSettings, setSmtpSettings] = useState({
        enabled: false,
        smtpHost: '',
        smtpPort: 587,
        smtpSecure: false,
        smtpUsername: '',
        smtpPassword: '',
        hasPassword: false,
        fromEmail: '',
        fromName: '',
        adminNotificationEmail: '',
        notifyOnNewOrder: true,
        notifyOnPaymentReceived: true,
    });

    const uploadLogo = (file) => {
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            setStoreInfo((prev) => ({ ...prev, logo_url: String(reader.result || '') }));
        };
        reader.readAsDataURL(file);
    };

    const load = async () => {
        setLoading(true);
        const [tenantRes, storesRes, smtpRes] = await Promise.all([
            tenantsApi.me(),
            storesApi.list({ limit: 500 }),
            smtpSettingsApi.getMySettings(),
        ]);

        const tenantData = tenantRes?.data || null;
        const storeRows = storesRes?.data?.data || [];

        setTenant(tenantData);
        setStores(storeRows);

        if (tenantData) {
            const meta = tenantData.metadata || {};
            const themeData = {
                primary: meta.theme?.primary || DEFAULT_THEME.primary,
                primary700: meta.theme?.primary700 || DEFAULT_THEME.primary700,
                darkBtn: meta.theme?.darkBtn || DEFAULT_THEME.darkBtn,
            };
            setStoreInfo({
                name: tenantData.name || '',
                email: tenantData.email || '',
                phone: tenantData.phone || '',
                logo_url: tenantData.logo_url || meta.logo_url || '',
                business_type: tenantData.business_type || '',
                address: meta.address || '',
            });
            setTheme(themeData);
            applyTheme(themeData);
        }

        if (storeRows.length > 0) {
            setTaxForm({ store_id: String(storeRows[0].id), tax_rate: String(Number(storeRows[0].tax_rate || 0)) });
            setSelectedStoreId(String(storeRows[0].id));

            const paymentRes = await paymentConfigApi.getStoreConfig(storeRows[0].id);
            if (paymentRes.success) {
                setPaymentConfig((prev) => ({
                    ...prev,
                    ...paymentRes.data,
                    paystack: {
                        ...prev.paystack,
                        ...(paymentRes.data?.paystack || {}),
                    },
                    flutterwave: {
                        ...prev.flutterwave,
                        ...(paymentRes.data?.flutterwave || {}),
                    },
                    bankTransfer: {
                        ...prev.bankTransfer,
                        ...(paymentRes.data?.bankTransfer || {}),
                    },
                }));
            }
        }

        if (smtpRes?.success && smtpRes?.data) {
            setSmtpSettings((prev) => ({
                ...prev,
                ...smtpRes.data,
                smtpPassword: '',
            }));
        }

        setLoading(false);
    };

    useEffect(() => {
        load();
    }, []);

    const selectedTaxStore = useMemo(
        () => stores.find((item) => String(item.id) === String(taxForm.store_id)),
        [stores, taxForm.store_id]
    );

    const selectedStore = useMemo(
        () => stores.find((item) => String(item.id) === String(selectedStoreId)),
        [stores, selectedStoreId]
    );

    const frontendStoreUrl = useMemo(() => {
        const origin = typeof window !== 'undefined' ? window.location.origin : '';
        if (!selectedStoreId) return origin;
        return `${origin}/store/${selectedStoreId}`;
    }, [selectedStoreId]);

    const saveStoreInfo = async () => {
        setSaving(true);
        const response = await tenantsApi.updateMe({
            name: storeInfo.name,
            email: storeInfo.email,
            phone: storeInfo.phone,
            logo_url: storeInfo.logo_url,
            business_type: storeInfo.business_type,
            metadata: {
                ...(tenant?.metadata || {}),
                address: storeInfo.address,
                logo_url: storeInfo.logo_url,
                theme,
            },
        });
        setSaving(false);
        if (!response.success) {
            window.alert(response.error || 'Could not save app settings');
            return;
        }
        setTenant(response.data);
        window.alert('App settings saved');
    };

    const createBranch = async (event) => {
        event.preventDefault();
        if (!branchForm.name) {
            window.alert('Branch name is required');
            return;
        }

        const response = await storesApi.create(branchForm);
        if (!response.success) {
            window.alert(response.error || 'Could not create branch');
            return;
        }

        setBranchForm({
            name: '',
            email: '',
            phone: '',
            address: '',
            city: '',
            state: '',
            country: 'Nigeria',
            store_type: 'retail',
        });
        load();
        window.alert('Branch created successfully');
    };

    const saveTaxRate = async () => {
        if (!taxForm.store_id) {
            window.alert('Select a branch/store first');
            return;
        }

        const response = await storesApi.update(Number(taxForm.store_id), { tax_rate: Number(taxForm.tax_rate || 0) });
        if (!response.success) {
            window.alert(response.error || 'Could not update tax rate');
            return;
        }
        load();
        window.alert('Tax rate updated');
    };

    const loadPaymentConfig = async (storeId) => {
        if (!storeId) return;
        const response = await paymentConfigApi.getStoreConfig(storeId);
        if (!response.success) {
            window.alert(response.error || 'Could not load payment settings');
            return;
        }
        setPaymentConfig((prev) => ({
            ...prev,
            ...response.data,
            paystack: { ...prev.paystack, ...(response.data?.paystack || {}) },
            flutterwave: { ...prev.flutterwave, ...(response.data?.flutterwave || {}) },
            bankTransfer: { ...prev.bankTransfer, ...(response.data?.bankTransfer || {}) },
        }));
    };

    const saveFrontendAndPayments = async () => {
        if (!selectedStoreId) {
            window.alert('Select a store first');
            return;
        }

        const targetStoreId = Number(selectedStoreId);
        const [frontRes, paystackRes, flutterRes, bankRes] = await Promise.all([
            paymentConfigApi.setStoreFrontend(targetStoreId, Boolean(paymentConfig.storeFrontendEnabled)),
            paymentConfigApi.updatePaystack(targetStoreId, paymentConfig.paystack),
            paymentConfigApi.updateFlutterwave(targetStoreId, paymentConfig.flutterwave),
            paymentConfigApi.updateBankTransfer(targetStoreId, paymentConfig.bankTransfer),
        ]);

        const failed = [frontRes, paystackRes, flutterRes, bankRes].find((item) => !item?.success);
        if (failed) {
            window.alert(failed.error || 'Failed to save payment settings');
            return;
        }

        window.alert('Store frontend and payment settings saved');
        loadPaymentConfig(targetStoreId);
    };

    const createAdminUser = async (event) => {
        event.preventDefault();
        if (!adminForm.first_name || !adminForm.last_name || !adminForm.email || !adminForm.password) {
            window.alert('First name, last name, email and password are required');
            return;
        }
        const response = await usersApi.create({ ...adminForm });
        if (!response.success) {
            window.alert(response.error || 'Could not create admin user');
            return;
        }
        setAdminForm({ first_name: '', last_name: '', email: '', phone: '', password: '', role: 'manager' });
        window.alert('Admin user created successfully');
    };

    const saveSmtpSettings = async () => {
        const response = await smtpSettingsApi.saveMySettings(smtpSettings);
        if (!response.success) {
            window.alert(response.error || 'Could not save SMTP settings');
            return;
        }

        setSmtpSettings((prev) => ({
            ...prev,
            ...(response.data || {}),
            smtpPassword: '',
        }));
        window.alert('SMTP settings saved');
    };

    const sendSmtpTest = async () => {
        const response = await smtpSettingsApi.sendTestEmail(smtpSettings.adminNotificationEmail || '');
        if (!response.success) {
            window.alert(response.error || 'SMTP test failed');
            return;
        }
        window.alert(response.message || 'Test email sent');
    };

    if (loading) return <div className="panel">Loading app settings...</div>;

    return (
        <div className="products-page">
            <div className="page-header products-header">
                <div>
                    <h2>App Settings</h2>
                    <p>Dashboard <span className="crumb-sep">›</span> App Settings</p>
                </div>
                <div className="products-toolbar-actions">
                    <button className="mini-icon-btn" type="button" onClick={load}>↻</button>
                    <button className="btn" type="button" onClick={saveStoreInfo} disabled={saving}>{saving ? 'Saving...' : 'Save Settings'}</button>
                </div>
            </div>

            <section className="products-shell stock-shell settings-shell">
                <div className="panel stock-panel settings-hero-panel">
                    <div className="settings-hero-copy">
                        <span>Store Admin Portal</span>
                        <h3>{storeInfo.name || 'Your Store'}</h3>
                        <p>Manage public storefront identity, branches, payments, tax, email automation, and admin access from one place.</p>
                    </div>
                    <div className="settings-hero-logo">
                        {storeInfo.logo_url ? <img src={storeInfo.logo_url} alt="" /> : <span>{String(storeInfo.name || 'R').slice(0, 1)}</span>}
                    </div>
                </div>

                <div className="panel stock-panel settings-card">
                    <div className="settings-section-title">
                        <h3>Store Information</h3>
                        <p>Shown on receipts, storefront, and order notifications.</p>
                    </div>
                    <div className="stock-form-grid">
                        <label>
                            <span>Business Name</span>
                            <input value={storeInfo.name} onChange={(e) => setStoreInfo((prev) => ({ ...prev, name: e.target.value }))} />
                        </label>
                        <label>
                            <span>Business Email</span>
                            <input type="email" value={storeInfo.email} onChange={(e) => setStoreInfo((prev) => ({ ...prev, email: e.target.value }))} />
                        </label>
                        <label>
                            <span>Business Phone</span>
                            <input value={storeInfo.phone} onChange={(e) => setStoreInfo((prev) => ({ ...prev, phone: e.target.value }))} />
                        </label>
                        <label>
                            <span>Business Type</span>
                            <input value={storeInfo.business_type} onChange={(e) => setStoreInfo((prev) => ({ ...prev, business_type: e.target.value }))} />
                        </label>
                        <label>
                            <span>Store Address</span>
                            <input value={storeInfo.address} onChange={(e) => setStoreInfo((prev) => ({ ...prev, address: e.target.value }))} />
                        </label>
                        <label>
                            <span>Logo URL</span>
                            <input value={storeInfo.logo_url} onChange={(e) => setStoreInfo((prev) => ({ ...prev, logo_url: e.target.value }))} placeholder="https://..." />
                        </label>
                        <label>
                            <span>Upload Logo</span>
                            <input type="file" accept="image/*" onChange={(e) => uploadLogo(e.target.files?.[0])} />
                        </label>
                    </div>
                    {storeInfo.logo_url ? (
                        <div className="settings-logo-preview">
                            <p className="muted-sm">Logo Preview</p>
                            <img src={storeInfo.logo_url} alt="Store logo preview" />
                        </div>
                    ) : null}
                </div>

                <div className="stock-grid-2">
                    <div className="panel stock-panel settings-card">
                        <div className="settings-section-title">
                            <h3>Brand Colors</h3>
                            <p>Applies instantly to your admin and storefront preview.</p>
                        </div>
                        <div className="stock-form-grid" style={{ gridTemplateColumns: '1fr' }}>
                            <label>
                                <span>Primary Color</span>
                                <input type="color" value={theme.primary} onChange={(e) => {
                                    const value = e.target.value;
                                    const next = { ...theme, primary: value };
                                    setTheme(next);
                                    applyTheme(next);
                                }} />
                            </label>
                            <label>
                                <span>Primary Dark</span>
                                <input type="color" value={theme.primary700} onChange={(e) => {
                                    const value = e.target.value;
                                    const next = { ...theme, primary700: value };
                                    setTheme(next);
                                    applyTheme(next);
                                }} />
                            </label>
                            <label>
                                <span>Button Dark Color</span>
                                <input type="color" value={theme.darkBtn} onChange={(e) => {
                                    const value = e.target.value;
                                    const next = { ...theme, darkBtn: value };
                                    setTheme(next);
                                    applyTheme(next);
                                }} />
                            </label>
                        </div>
                    </div>

                    <div className="panel stock-panel settings-card">
                        <div className="settings-section-title">
                            <h3>POS Tax Rate</h3>
                            <p>Set branch tax used on POS and storefront orders.</p>
                        </div>
                        <div className="stock-form-grid" style={{ gridTemplateColumns: '1fr' }}>
                            <label>
                                <span>Branch / Store</span>
                                <select value={taxForm.store_id} onChange={(e) => {
                                    const storeId = e.target.value;
                                    const store = stores.find((item) => String(item.id) === String(storeId));
                                    setTaxForm({
                                        store_id: storeId,
                                        tax_rate: String(Number(store?.tax_rate || 0)),
                                    });
                                }}>
                                    {stores.map((item) => (
                                        <option key={item.id} value={item.id}>{item.name}</option>
                                    ))}
                                </select>
                            </label>
                            <label>
                                <span>Tax Rate (%)</span>
                                <input type="number" min="0" max="100" step="0.01" value={taxForm.tax_rate} onChange={(e) => setTaxForm((prev) => ({ ...prev, tax_rate: e.target.value }))} />
                            </label>
                            <p className="muted-sm">Current: {selectedTaxStore ? `${selectedTaxStore.name} (${Number(selectedTaxStore.tax_rate || 0).toFixed(2)}%)` : '-'}</p>
                            <button className="btn" type="button" onClick={saveTaxRate}>Update Tax Rate</button>
                        </div>
                    </div>
                </div>

                <div className="panel stock-panel settings-card">
                    <div className="settings-section-title">
                        <h3>Create Branch / Warehouse</h3>
                        <p>Add selling locations, stock points, and customer pickup branches.</p>
                    </div>
                    <form className="stock-form-grid" onSubmit={createBranch}>
                        <label>
                            <span>Name</span>
                            <input value={branchForm.name} onChange={(e) => setBranchForm((prev) => ({ ...prev, name: e.target.value }))} required />
                        </label>
                        <label>
                            <span>Email</span>
                            <input type="email" value={branchForm.email} onChange={(e) => setBranchForm((prev) => ({ ...prev, email: e.target.value }))} />
                        </label>
                        <label>
                            <span>Phone</span>
                            <input value={branchForm.phone} onChange={(e) => setBranchForm((prev) => ({ ...prev, phone: e.target.value }))} />
                        </label>
                        <label>
                            <span>Address</span>
                            <input value={branchForm.address} onChange={(e) => setBranchForm((prev) => ({ ...prev, address: e.target.value }))} />
                        </label>
                        <label>
                            <span>City</span>
                            <input value={branchForm.city} onChange={(e) => setBranchForm((prev) => ({ ...prev, city: e.target.value }))} />
                        </label>
                        <label>
                            <span>State</span>
                            <input value={branchForm.state} onChange={(e) => setBranchForm((prev) => ({ ...prev, state: e.target.value }))} />
                        </label>
                        <label>
                            <span>Country</span>
                            <input value={branchForm.country} onChange={(e) => setBranchForm((prev) => ({ ...prev, country: e.target.value }))} />
                        </label>
                        <label>
                            <span>Type</span>
                            <select value={branchForm.store_type} onChange={(e) => setBranchForm((prev) => ({ ...prev, store_type: e.target.value }))}>
                                <option value="retail">Retail Branch</option>
                                <option value="warehouse">Warehouse</option>
                                <option value="outlet">Outlet</option>
                            </select>
                        </label>
                        <div className="stock-form-actions">
                            <button className="btn" type="submit">Create Branch</button>
                        </div>
                    </form>
                </div>

                <div className="panel stock-panel settings-card">
                    <div className="settings-section-title">
                        <h3>Store Frontend & Payment Settings</h3>
                        <p>Enable ecommerce and configure Paystack, Flutterwave, and bank transfer.</p>
                    </div>
                    <div className="stock-form-grid">
                        <label>
                            <span>Store</span>
                            <select value={selectedStoreId} onChange={(e) => {
                                const storeId = e.target.value;
                                setSelectedStoreId(storeId);
                                loadPaymentConfig(Number(storeId));
                            }}>
                                {stores.map((item) => (
                                    <option key={item.id} value={item.id}>{item.name}</option>
                                ))}
                            </select>
                        </label>
                        <label>
                            <span>Enable Public Store Frontend</span>
                            <select value={paymentConfig.storeFrontendEnabled ? 'yes' : 'no'} onChange={(e) => setPaymentConfig((prev) => ({ ...prev, storeFrontendEnabled: e.target.value === 'yes' }))}>
                                <option value="yes">Yes</option>
                                <option value="no">No</option>
                            </select>
                        </label>
                        <label style={{ gridColumn: '1 / -1' }}>
                            <span>Frontend Store URL</span>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                <input value={frontendStoreUrl} readOnly />
                                <a
                                    className="btn btn-outline"
                                    href={frontendStoreUrl || '#'}
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={(e) => { if (!frontendStoreUrl) e.preventDefault(); }}
                                >
                                    Open URL
                                </a>
                            </div>
                            <small className="field-hint">Share this link with customers after enabling the public store frontend.</small>
                        </label>

                        <label>
                            <span>Paystack Enabled</span>
                            <select value={paymentConfig.paystack.enabled ? 'yes' : 'no'} onChange={(e) => setPaymentConfig((prev) => ({ ...prev, paystack: { ...prev.paystack, enabled: e.target.value === 'yes' } }))}>
                                <option value="yes">Yes</option>
                                <option value="no">No</option>
                            </select>
                        </label>
                        <label>
                            <span>Paystack Public Key</span>
                            <input value={paymentConfig.paystack.publicKey || ''} onChange={(e) => setPaymentConfig((prev) => ({ ...prev, paystack: { ...prev.paystack, publicKey: e.target.value } }))} />
                        </label>
                        <label>
                            <span>Paystack Secret Key</span>
                            <input value={paymentConfig.paystack.secretKey || ''} onChange={(e) => setPaymentConfig((prev) => ({ ...prev, paystack: { ...prev.paystack, secretKey: e.target.value } }))} />
                        </label>

                        <label>
                            <span>Flutterwave Enabled</span>
                            <select value={paymentConfig.flutterwave.enabled ? 'yes' : 'no'} onChange={(e) => setPaymentConfig((prev) => ({ ...prev, flutterwave: { ...prev.flutterwave, enabled: e.target.value === 'yes' } }))}>
                                <option value="yes">Yes</option>
                                <option value="no">No</option>
                            </select>
                        </label>
                        <label>
                            <span>Flutterwave Public Key</span>
                            <input value={paymentConfig.flutterwave.publicKey || ''} onChange={(e) => setPaymentConfig((prev) => ({ ...prev, flutterwave: { ...prev.flutterwave, publicKey: e.target.value } }))} />
                        </label>
                        <label>
                            <span>Flutterwave Secret Key</span>
                            <input value={paymentConfig.flutterwave.secretKey || ''} onChange={(e) => setPaymentConfig((prev) => ({ ...prev, flutterwave: { ...prev.flutterwave, secretKey: e.target.value } }))} />
                        </label>
                        <label>
                            <span>Flutterwave Encryption Key</span>
                            <input value={paymentConfig.flutterwave.encryptionKey || ''} onChange={(e) => setPaymentConfig((prev) => ({ ...prev, flutterwave: { ...prev.flutterwave, encryptionKey: e.target.value } }))} />
                        </label>

                        <label>
                            <span>Bank Transfer Enabled</span>
                            <select value={paymentConfig.bankTransfer.enabled ? 'yes' : 'no'} onChange={(e) => setPaymentConfig((prev) => ({ ...prev, bankTransfer: { ...prev.bankTransfer, enabled: e.target.value === 'yes' } }))}>
                                <option value="yes">Yes</option>
                                <option value="no">No</option>
                            </select>
                        </label>
                        <label>
                            <span>Bank Name</span>
                            <input value={paymentConfig.bankTransfer.bankName || ''} onChange={(e) => setPaymentConfig((prev) => ({ ...prev, bankTransfer: { ...prev.bankTransfer, bankName: e.target.value } }))} />
                        </label>
                        <label>
                            <span>Account Name</span>
                            <input value={paymentConfig.bankTransfer.accountName || ''} onChange={(e) => setPaymentConfig((prev) => ({ ...prev, bankTransfer: { ...prev.bankTransfer, accountName: e.target.value } }))} />
                        </label>
                        <label>
                            <span>Account Number</span>
                            <input value={paymentConfig.bankTransfer.accountNumber || ''} onChange={(e) => setPaymentConfig((prev) => ({ ...prev, bankTransfer: { ...prev.bankTransfer, accountNumber: e.target.value } }))} />
                        </label>
                        <label>
                            <span>Transfer Instructions</span>
                            <input value={paymentConfig.bankTransfer.instructions || ''} onChange={(e) => setPaymentConfig((prev) => ({ ...prev, bankTransfer: { ...prev.bankTransfer, instructions: e.target.value } }))} />
                        </label>
                        <label>
                            <span>Receipt Required For Bank Transfer</span>
                            <select value={paymentConfig.bankTransfer.receiptRequired ? 'yes' : 'no'} onChange={(e) => setPaymentConfig((prev) => ({ ...prev, bankTransfer: { ...prev.bankTransfer, receiptRequired: e.target.value === 'yes' } }))}>
                                <option value="yes">Yes</option>
                                <option value="no">No</option>
                            </select>
                        </label>
                    </div>
                    <div className="stock-form-actions" style={{ marginTop: 12 }}>
                        <button className="btn" type="button" onClick={saveFrontendAndPayments}>Save Store Frontend & Payments</button>
                        <span className="muted-sm">Target: {selectedStore?.name || 'No store selected'}</span>
                    </div>
                </div>

                <div className="panel stock-panel settings-card">
                    <div className="settings-section-title">
                        <h3>Email Automation (SMTP)</h3>
                        <p>Send order alerts and customer confirmations from your own email.</p>
                    </div>
                    <div className="stock-form-grid">
                        <label>
                            <span>Enable SMTP Notifications</span>
                            <select value={smtpSettings.enabled ? 'yes' : 'no'} onChange={(e) => setSmtpSettings((prev) => ({ ...prev, enabled: e.target.value === 'yes' }))}>
                                <option value="yes">Yes</option>
                                <option value="no">No</option>
                            </select>
                        </label>
                        <label>
                            <span>SMTP Host</span>
                            <input value={smtpSettings.smtpHost || ''} onChange={(e) => setSmtpSettings((prev) => ({ ...prev, smtpHost: e.target.value }))} placeholder="smtp.gmail.com" />
                        </label>
                        <label>
                            <span>SMTP Port</span>
                            <input type="number" value={smtpSettings.smtpPort || 587} onChange={(e) => setSmtpSettings((prev) => ({ ...prev, smtpPort: Number(e.target.value || 587) }))} />
                        </label>
                        <label>
                            <span>Use SSL/TLS</span>
                            <select value={smtpSettings.smtpSecure ? 'yes' : 'no'} onChange={(e) => setSmtpSettings((prev) => ({ ...prev, smtpSecure: e.target.value === 'yes' }))}>
                                <option value="yes">Yes</option>
                                <option value="no">No</option>
                            </select>
                        </label>
                        <label>
                            <span>SMTP Username</span>
                            <input value={smtpSettings.smtpUsername || ''} onChange={(e) => setSmtpSettings((prev) => ({ ...prev, smtpUsername: e.target.value }))} />
                        </label>
                        <label>
                            <span>SMTP Password {smtpSettings.hasPassword ? '(leave blank to keep existing)' : ''}</span>
                            <input type="password" value={smtpSettings.smtpPassword || ''} onChange={(e) => setSmtpSettings((prev) => ({ ...prev, smtpPassword: e.target.value }))} />
                        </label>
                        <label>
                            <span>From Email</span>
                            <input type="email" value={smtpSettings.fromEmail || ''} onChange={(e) => setSmtpSettings((prev) => ({ ...prev, fromEmail: e.target.value }))} />
                        </label>
                        <label>
                            <span>From Name</span>
                            <input value={smtpSettings.fromName || ''} onChange={(e) => setSmtpSettings((prev) => ({ ...prev, fromName: e.target.value }))} />
                        </label>
                        <label>
                            <span>Admin Notification Email</span>
                            <input type="email" value={smtpSettings.adminNotificationEmail || ''} onChange={(e) => setSmtpSettings((prev) => ({ ...prev, adminNotificationEmail: e.target.value }))} />
                        </label>
                        <label>
                            <span>Notify Admin On New Storefront Order</span>
                            <select value={smtpSettings.notifyOnNewOrder ? 'yes' : 'no'} onChange={(e) => setSmtpSettings((prev) => ({ ...prev, notifyOnNewOrder: e.target.value === 'yes' }))}>
                                <option value="yes">Yes</option>
                                <option value="no">No</option>
                            </select>
                        </label>
                        <label>
                            <span>Notify Admin On Payment Received</span>
                            <select value={smtpSettings.notifyOnPaymentReceived ? 'yes' : 'no'} onChange={(e) => setSmtpSettings((prev) => ({ ...prev, notifyOnPaymentReceived: e.target.value === 'yes' }))}>
                                <option value="yes">Yes</option>
                                <option value="no">No</option>
                            </select>
                        </label>
                    </div>
                    <div className="stock-form-actions" style={{ marginTop: 12 }}>
                        <button className="btn" type="button" onClick={saveSmtpSettings}>Save SMTP Settings</button>
                        <button className="btn btn-outline" type="button" onClick={sendSmtpTest}>Send Test Email</button>
                    </div>
                    <p className="muted-sm" style={{ marginTop: 8 }}>SMTP settings are stored in your database and used for storefront checkout/customer-admin notifications.</p>
                </div>

                {user?.role === 'admin' ? (
                    <div className="panel stock-panel settings-card">
                        <div className="settings-section-title">
                            <h3>Create Manager/Admin User</h3>
                            <p>Invite trusted team members to operate branches and settings.</p>
                        </div>
                        <form className="stock-form-grid" onSubmit={createAdminUser}>
                            <label>
                                <span>First Name</span>
                                <input value={adminForm.first_name} onChange={(e) => setAdminForm((prev) => ({ ...prev, first_name: e.target.value }))} />
                            </label>
                            <label>
                                <span>Last Name</span>
                                <input value={adminForm.last_name} onChange={(e) => setAdminForm((prev) => ({ ...prev, last_name: e.target.value }))} />
                            </label>
                            <label>
                                <span>Email</span>
                                <input type="email" value={adminForm.email} onChange={(e) => setAdminForm((prev) => ({ ...prev, email: e.target.value }))} />
                            </label>
                            <label>
                                <span>Phone</span>
                                <input value={adminForm.phone} onChange={(e) => setAdminForm((prev) => ({ ...prev, phone: e.target.value }))} />
                            </label>
                            <label>
                                <span>Password</span>
                                <input type="password" value={adminForm.password} onChange={(e) => setAdminForm((prev) => ({ ...prev, password: e.target.value }))} />
                            </label>
                            <label>
                                <span>Role</span>
                                <select value={adminForm.role} onChange={(e) => setAdminForm((prev) => ({ ...prev, role: e.target.value }))}>
                                    <option value="manager">Manager</option>
                                    <option value="admin">Admin</option>
                                </select>
                            </label>
                            <div className="stock-form-actions">
                                <button className="btn" type="submit">Create User</button>
                            </div>
                        </form>
                    </div>
                ) : null}
            </section>
        </div>
    );
}
